import { BootstrapVue, IconsPlugin } from "bootstrap-vue";
import jwt_decode from "jwt-decode";
import Vue from "vue";
import VueAnalytics from "vue-analytics";
import vSelect from "vue-select";
import App from "./App.vue";
import "./assets/app.scss";
import { GTAG_APP_ID } from "./config/index.js";
import EventBus from "./core/EventBus";
import { polishedDomain } from "./core/utils/index";
import { i18n, loadLanguageAsync } from "./plugins/i18n";
import router from "./router";
import store from "./store";

Vue.component("v-select", vSelect);

Vue.config.productionTip = false;

// Install BootstrapVue
Vue.use(BootstrapVue);
// Optionally install the BootstrapVue icon components plugin
Vue.use(IconsPlugin);

Vue.use(i18n);

// Configuration VueAnalytics to add Google Anayltics
Vue.use(VueAnalytics, {
  id: GTAG_APP_ID,
  router,
  beforeFirstHit() {
    // Anonoymize the IP for GDPR concern
    Vue.$ga.set("anonymizeIp", true);
  }
});

/**
 * Route guard the authorization meta field present within the router.
 * Routes will redirect back to the root of our application,
 * whenever the isLoggedIn state is false. This only applies when the
 * "auth" meta field is available within the designated route.
 */
router.beforeEach(async (to, from, next) => {
  if (!to.params.game) {
    if (to.path === "/game/notfound") return next();
    return next("/game/notfound");
  }

  // Fetch game config if necessary
  await store.dispatch("fetchGameConfig", to.params.game);

  // Fetch language if necessary
  await loadLanguageAsync(store.getters.getLocale);

  if (to.path === "/" || to.name === "Home") {
    // Our games will be implemented on gd wrapper as .../Klondike?domain=TOP_DOMAIN
    // So, the domain is supposed to be there always, otherwise use default.
    const domain = to.query.domain
      ? polishedDomain(to.query.domain)
      : "gamedistribution.com";

    const locale = to.query.locale
      ? to.query.locale.slice(0, 2)
      : store.getters.getLocale
      ? store.getters.getLocale.slice(0, 2)
      : "en";

    window.localStorage.setItem("gd_plus_domain", domain);
    window.localStorage.setItem("gd_plus_locale", locale.toLowerCase());
    store.commit("POPULATE_DOMAIN");
    store.commit("POPULATE_LOCALE");

    // We will may another game url based on platform
    // Thus, remove game config and fetch again
    window.localStorage.removeItem("gd_plus_game");
    await store.dispatch("fetchGameConfig", to.params.game);

    // If we use sso integration, then just store sso token and start using it
    // If the token is not valid, we already send the user to the login
    const sso = to.query.sso;
    if (sso) {
      window.localStorage.setItem("gd_plus_token", sso);
      // Commit to the state so that we wont show logout for sso partners
      store.commit("SSO_LOGIN");
      await store.dispatch("populateUser", sso);
    } else {
      // Remove if there is an sso token on localstorage so that
      // We do not confuse it with psgn token
      const token = store.state.token;
      if (token) {
        const data = jwt_decode(token);
        if (data.role === "sso") store.commit("LOGOUT");
      }
    }

    if (store.state.isLoggedIn) {
      next(`/${to.params.game}/play`);
    } else {
      // Create a guest user and send the user into game
      store.dispatch("loginGuest").then(() => {
        next(`/${to.params.game}/play`);
      });
    }
  } else if (to.path === `/${to.params.game}/login` && store.state.isLoggedIn) {
    next(`/${to.params.game}/play`);
  } else if (!store.state.isLoggedIn) {
    next(`/${to.params.game}`);
  } else {
    next();
  }
});

window.GDPLUS_EVENTBUS = new EventBus();

new Vue({
  router,
  store,
  i18n,
  render: h => h(App)
}).$mount("#app");
